import { useState } from "react";
import { useEffect } from "react";
import { EmailIcon, EmailShareButton, FacebookIcon, FacebookShareButton } from "react-share";

import CopyIcon from "@mui/icons-material/ContentCopy";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import { Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import gtag from "ga-gtag";
import { useSnackbar } from "notistack";

import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";

const SharedInvitationDialog = props => {
  const {
    open,
    onClose,
    onAccept,
    dialogTitle = "Share Invitation",
    okMessage = "Ok",
    maxWidth = "xs",
    fullWidth = true,
    sharedInvitationKey = {},
    invitationSharingVars = null,
  } = props;

  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();

  const smallScreen = useMediaQuery(theme => theme.breakpoints.down("md"));

  const superSmallScreen = useMediaQuery(theme => theme.breakpoints.down("sm"));

  const [emailMessage, setEmailMessage] = useState(null);
  const [smsMessage, setSMSMessage] = useState(null);
  const [facebookMessage, setFacebookMessage] = useState(null);

  useEffect(() => {
    // We're going to set up the message variables in one
    // function
    try {
      invitationSharingVars.forEach(obj => {
        if (obj.key === "email") {
          setEmailMessage(obj);
        } else if (obj.key === "sms") {
          setSMSMessage(obj);
        }
      });
    } catch (error) {
      console.log(error);
    }
  }, [invitationSharingVars, setEmailMessage, setSMSMessage]);

  useEffect(() => {
    // We're going to set up the message variables in one
    // function
    try {
      invitationSharingVars.forEach(obj => {
        if (obj.key === "email") {
          setEmailMessage(obj);
        } else if (obj.key === "sms") {
          setSMSMessage(obj);
        } else if (obj.key === "facebook") {
          setFacebookMessage(obj);
        }
      });
    } catch (error) {
      console.log(error);
    }
  }, [invitationSharingVars, setEmailMessage, setSMSMessage]);

  const onAcceptHandler = () => {
    onAccept();
  };

  const onCloseHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Grid container justifyContent={"space-evenly"} spacing={2}>
          <Grid item xs={12}>
            Share an Invitation Link by clicking an icon below:
          </Grid>
          {emailMessage && (
            <Grid container item justifyContent={"space-around"} alignContent={"center"} xs={2}>
              <Grid item>
                <Tooltip title="Send a link via email">
                  <EmailShareButton
                    subject={emailMessage.invitationSubject}
                    body={emailMessage.invitationBody}
                    separator=""
                    url={`${window.location.origin}/onboarding?inviteKey=${sharedInvitationKey}`}
                  >
                    <EmailIcon
                      borderRadius={2}
                      bgStyle={{ fill: theme.palette.primary.main }}
                      size={superSmallScreen ? 48 : 64}
                      onClick={() => {
                        gtag("event", "omni_invite_sharing_email", {
                          invite_key: sharedInvitationKey,
                        });
                      }}
                    />
                  </EmailShareButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Typography variant="overline" textAlign={"center"} sx={{ width: "100%" }}>
                  EMail
                </Typography>
              </Grid>
            </Grid>
          )}

          <Grid container item justifyContent={"space-around"} alignContent={"center"} xs={2}>
            <Grid item>
              <Tooltip title="Copy invite link to clipboard">
                <Grid
                  item
                  sx={{
                    borderRadius: "2px",
                    height: superSmallScreen ? 48 : 64,
                    width: superSmallScreen ? 48 : 64,
                    backgroundColor: "#41555F",
                  }}
                  onClick={() => {
                    let onboardUrl = `${window.location.origin}/onboarding?inviteKey=${sharedInvitationKey}`;
                    navigator.clipboard.writeText(onboardUrl);

                    gtag("event", "omni_invite_sharing_copy", {
                      invite_key: sharedInvitationKey,
                    });

                    enqueueSnackbar("Invite URL Copied to Clipboard", {
                      variant: "success",
                    });
                  }}
                >
                  <CopyIcon
                    sx={{
                      fill: "#fff",
                      height: superSmallScreen ? "48px" : "64px",
                      width: "30px",
                      marginLeft: superSmallScreen ? "10px" : "17px",
                      marginRight: superSmallScreen ? "10px" : "17px",
                    }}
                  />
                </Grid>
              </Tooltip>
            </Grid>
            <Grid item sx={{ paddingTop: "4px" }}>
              <Typography variant="overline" textAlign={"center"} sx={{ width: "100%" }}>
                Copy
              </Typography>
            </Grid>
          </Grid>
          {facebookMessage && (
            <Grid container item justifyContent={"space-around"} alignContent={"center"} xs={2}>
              <Grid item>
                <Tooltip title="Share invite on Facebook">
                  <FacebookShareButton
                    url={`${window.location.origin}/onboarding?inviteKey=${sharedInvitationKey}`}
                    quote={facebookMessage.invitationBody}
                    onClick={() => {
                      gtag("event", "omni_invite_sharing_facebook", {
                        invite_key: sharedInvitationKey,
                      });
                    }}
                  >
                    <FacebookIcon borderRadius={2} size={superSmallScreen ? 48 : 64} />
                  </FacebookShareButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Typography variant="overline" textAlign={"center"} sx={{ width: "100%" }}>
                  Facebook
                </Typography>
              </Grid>
            </Grid>
          )}
          {smallScreen && smsMessage && (
            <Grid container item justifyContent={"space-around"} alignContent={"center"} xs={2}>
              <Grid item>
                <a
                  href={`sms:?&body=${smsMessage.invitationBody}${window.location.origin}/onboarding?inviteKey=${sharedInvitationKey}`}
                >
                  <Tooltip direction="right" title="Send a link using SMS (text)">
                    <Grid
                      item
                      sx={{
                        borderRadius: "2px",
                        height: superSmallScreen ? 48 : 64,
                        width: superSmallScreen ? 48 : 64,
                        backgroundColor: "#7C529E",
                      }}
                    >
                      <SmsRoundedIcon
                        sx={{
                          fill: "#fff",
                          height: superSmallScreen ? "48px" : "64px",
                          width: "30px",
                          marginLeft: superSmallScreen ? "9px" : "17px",
                          marginRight: superSmallScreen ? "10px" : "17px",
                        }}
                      />
                    </Grid>
                  </Tooltip>
                </a>
              </Grid>
              <Grid item sx={{ paddingTop: "4px" }}>
                <Typography variant="overline" textAlign={"center"} sx={{ width: "100%" }}>
                  SMS
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onAcceptHandler} color="secondary" variant="contained">
          {okMessage}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharedInvitationDialog;
